import { useCallback } from 'react';
import { useAppSelector } from '../store/hooks';

/**
 * Gating for the LOGGED-IN user, against their own module grants.
 *
 * Mirrors the backend resolver (ibook-backend/src/middleware/moduleAccess.js →
 * userCan) exactly, so the UI never offers an action the API would reject and
 * never blocks one it would allow:
 *
 *   - enforcement OFF for the account => everything allowed (unchanged behaviour)
 *   - account owner                    => everything allowed
 *   - a module-level grant covers ALL of that module's sub-modules
 *   - otherwise a matching sub-module grant is required
 *
 * The grants themselves come from state.auth.modulePermissions, which the
 * loadUserPermissions thunk fills from GET /permissions/user/:id. They already
 * include the defaults the user's role provides.
 *
 * Note this is NOT the same as useModulePermissions — that hook is the admin
 * CRUD wrapper for editing ANOTHER user's permissions and issues requests on
 * mount. This one is read-only redux state and safe inside grid cells.
 */
export const useModuleAccess = () => {
    const { modulePermissions, selectedAccount } = useAppSelector((state) => state.auth);

    const enforceModuleAccess = !!(modulePermissions?.permissionEnforcement ?? selectedAccount?.permissionEnforcement);
    // Admin-level access is deliberately folded into the owner check: a user an
    // Admin has flagged with full access is unrestricted, exactly like the owner.
    const isAccountOwner = !!(modulePermissions?.isAccountOwner || modulePermissions?.hasFullAccess);

    /** Can the user perform `action` on this module (or one of its sub-modules)? */
    const can = useCallback(
        (moduleKey: string, action: string, subModuleKey?: string | null): boolean => {
            if (!enforceModuleAccess || isAccountOwner) return true;
            const ma = modulePermissions?.moduleAccess?.[moduleKey];
            if (!ma) return false;
            if (Array.isArray(ma.permissions) && ma.permissions.includes(action)) return true;
            if (subModuleKey && ma.subModules && ma.subModules[subModuleKey]) {
                const sub = ma.subModules[subModuleKey];
                return Array.isArray(sub.permissions) && sub.permissions.includes(action);
            }
            return false;
        },
        [enforceModuleAccess, isAccountOwner, modulePermissions]
    );

    /** Does the user have ANY access to the module? (sidebar-style visibility) */
    const canAccessModule = useCallback(
        (moduleKey: string): boolean => {
            if (!enforceModuleAccess || isAccountOwner) return true;
            const ma = modulePermissions?.moduleAccess?.[moduleKey];
            if (!ma) return false;
            const hasModuleLevel = Array.isArray(ma.permissions) && ma.permissions.length > 0;
            const hasSubModule = !!(ma.subModules && Object.keys(ma.subModules).length > 0);
            return hasModuleLevel || hasSubModule;
        },
        [enforceModuleAccess, isAccountOwner, modulePermissions]
    );

    return { can, canAccessModule, enforceModuleAccess, isAccountOwner };
};

export default useModuleAccess;
